import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Edit, RefreshCw, Inbox } from "react-feather";
import MasterDetailListShell from "../../../../components/layout/MasterDetailListShell";
import MasterDetailRow from "../../../../components/layout/MasterDetailRow";
import { useGetPettyCashFundsQuery, useListPettyCashRefillsQuery } from "../../pettyCashApi";
import { useCanManageFund, useCanRequestRefill, useCanViewPettyCash } from "../../usePettyCashPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const money = (n) => Number(n || 0).toFixed(2);

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "", label: "All Statuses" },
];

// One fund per branch (2026-08-27 redesign) - this list is short (one row
// per active branch) but still built on the same shared shell for visual
// consistency with Vouchers/Refills/Reconciliations. Defaults to
// Active-only (2026-08-29 fix) - a cancelled/deactivated fund (isActive=0,
// e.g. "Bank of Baroda (K) Ltd") was being fetched and shown identically to
// a live fund because the list called GET /pettycash/funds with no
// isActive filter at all; the API supports the filter, this just wasn't
// passing it.
const FundCompactList = () => {
  const canManage = useCanManageFund();
  const canRequestRefill = useCanRequestRefill();
  const canViewRefills = useCanViewPettyCash();
  const navigate = useNavigate();
  const { fundId: selectedFundId } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const { selectedBranchId } = useBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: funds = [], isLoading } = useGetPettyCashFundsQuery({
    branchId: selectedBranchId ?? undefined,
    isActive: status === "" ? undefined : status === "active",
  });

  // Just the count, for the badge on the button. Refills left the sidebar
  // (2026-09-08) and this page is now their only entry point, so an approver has
  // to be able to see from here that requests are waiting.
  const { data: pendingRefills } = useListPettyCashRefillsQuery(
    { status: "SUBMITTED", branchId: selectedBranchId ?? undefined, page: 1, pageSize: 1 },
    { skip: !canViewRefills }
  );
  const pendingCount = pendingRefills?.totalCount ?? 0;

  const filtered = useMemo(
    () =>
      funds.filter(
        (f) =>
          !search ||
          f.fundName.toLowerCase().includes(search.toLowerCase()) ||
          f.branchCode.toLowerCase().includes(search.toLowerCase())
      ),
    [funds, search]
  );

  return (
    <MasterDetailListShell
      title="Petty Cash Funds"
      addButton={
        /* "Add New Fund" was removed here on 2026-09-08 by instruction - there is one
           fund per branch and they all exist, so creating another is not something
           anyone should be doing from this screen. The route and FundForm are left
           intact and untouched (editing still uses them); only the button is gone.

           In its place: refills. They were taken out of the sidebar the same day, so
           requesting and finding them both start here, on the funds they act on. */
        <div className="d-flex gap-2">
          {canRequestRefill && (
            <button
              type="button"
              className="btn btn-added d-inline-flex align-items-center"
              onClick={() => navigate(paths.pettyCashRefillNew)}
            >
              <RefreshCw className="me-2" size={16} />
              Request Refill
            </button>
          )}
          {canViewRefills && (
            <button
              type="button"
              className="btn btn-outline-primary d-inline-flex align-items-center"
              onClick={() => navigate(paths.pettyCashRefills)}
            >
              <Inbox className="me-2" size={16} />
              Refill Requests
              {pendingCount > 0 && <span className="badge bg-danger ms-2">{pendingCount}</span>}
            </button>
          )}
        </div>
      }
      filters={
        <>
          <input
            type="text"
            placeholder="Search fund or branch"
            className="form-control form-control-sm"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <div className="d-flex gap-2 mt-2">
            <select className="form-control form-control-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </>
      }
      isLoading={isLoading}
      emptyLabel="No funds match these filters."
      items={filtered}
      getKey={(f) => f.fundId}
      resetKey={`${search}|${status}|${selectedBranchId}`}
      renderItem={(f) => {
        const isSelected = String(f.fundId) === String(selectedFundId);
        const isLow = f.currentBalance <= f.minimumBalance;
        return (
          <MasterDetailRow
            isSelected={isSelected}
            onClick={() => navigate(paths.pettyCashFundEdit(f.fundId))}
            avatarLabel={f.branchCode?.slice(0, 2).toUpperCase() || "?"}
            title={f.fundName}
            subtitle={
              <>{f.branchCode} &middot; Balance: {money(f.currentBalance)} {f.currencyCode}</>
            }
            badge={
              <span className="d-inline-flex gap-1 me-2">
                {!f.isActive && <span className="badge badge-linedanger">Inactive</span>}
                {f.isActive && (
                  <span className={`badge ${isLow ? "badge-linedanger" : "badge-linesuccess"}`}>
                    {isLow ? "Low" : "Normal"}
                  </span>
                )}
              </span>
            }
            actions={
              <>
                {/* Requesting from the row itself carries the branch across, so nobody
                    has to re-pick on the next screen which fund they meant. */}
                {canRequestRefill && f.isActive && (
                  <button
                    type="button"
                    className="btn btn-link p-1"
                    title={`Request a refill for ${f.fundName}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`${paths.pettyCashRefillNew}?branchId=${f.branchId}`);
                    }}
                  >
                    <RefreshCw size={15} />
                  </button>
                )}
                {canManage && (
                  <button
                    type="button"
                    className="btn btn-link p-1"
                    title="Edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(paths.pettyCashFundEdit(f.fundId));
                    }}
                  >
                    <Edit size={15} />
                  </button>
                )}
              </>
            }
          />
        );
      }}
    />
  );
};

export default FundCompactList;
